
/* === minimal-slider.js === */
if (!customElements.get('minimal-slider')) {
  const CONSTANTS = {
    CLONE_BUFFER: 2,
    RESIZE_DEBOUNCE_MS: 150,
    SWIPE_THRESHOLD_PX: 50,
    TRANSITION_TIMEOUT_MS: 1000,
    BREAKPOINT_DESKTOP: 1000,
    BREAKPOINT_TABLET: 750
  };

  customElements.define('minimal-slider', class MinimalSlider extends HTMLElement {
    constructor() {
      super();
      this.currentSlide = 0;
      this.autoplayTimer = null;
      this.isUserInteracting = false;
      this.isTransitioning = false;
      this.resizeHandler = null;
      this.navigationQueue = [];
      this.transitionEndHandler = null;
      this.transitionTimeout = null;
      this.cachedElements = null;
      this.cachedSlidesPerView = null;
      this.ariaLiveRegion = null;
    }

    connectedCallback() {
      this.autoplay = this.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 5000;
      this.loop = this.dataset.loop !== 'false';
      this.infiniteLoop = this.dataset.infiniteLoop === 'true';
      this.animationType = this.dataset.animationType || 'slide';
      this.clickNavigation = this.dataset.clickNavigation === 'true';

      this.track = this.querySelector('.minimal-slider__track');
      this.originalSlides = this.querySelectorAll('.minimal-slider__slide[data-slide-index]');
      this.totalOriginalSlides = this.originalSlides.length;

      if (this.infiniteLoop && this.loop && this.totalOriginalSlides > 1) {
        this.setupInfiniteLoop();
      }

      this.slides = this.querySelectorAll('.minimal-slider__slide');

      const navButtons = this.findNavButtons();
      this.prevBtn = navButtons.prev;
      this.nextBtn = navButtons.next;
      this.dots = this.findDots();

      this.totalSlides = this.slides.length;

      if (this.totalSlides <= 1) {
        if (this.prevBtn) this.prevBtn.style.visibility = 'hidden';
        if (this.nextBtn) this.nextBtn.style.visibility = 'hidden';
        this.dots.forEach(dot => dot.style.visibility = 'hidden');
        return;
      }

      this.init();
    }

    disconnectedCallback() {
      this.pauseAutoplay();
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }
      if (this.transitionEndHandler && this.track) {
        this.track.removeEventListener('transitionend', this.transitionEndHandler);
      }
      if (this.transitionTimeout) {
        clearTimeout(this.transitionTimeout);
      }
    }

    setupInfiniteLoop() {
      if (!this.track) return;

      const n = this.totalOriginalSlides;
      const slidesPerView = this.getSlidesPerView();
      const cloneCount = Math.max(slidesPerView + CONSTANTS.CLONE_BUFFER, n);

      const beforeClones = [];
      for (let i = 0; i < cloneCount; i++) {
        const sourceIndex = ((n - 1 - i) % n + n) % n;
        beforeClones.push(this.originalSlides[sourceIndex].cloneNode(true));
      }
      beforeClones.reverse();
      const firstOriginal = this.originalSlides[0];
      beforeClones.forEach(clone => {
        clone.classList.add('slide-clone', 'slide-clone--before');
        clone.removeAttribute('data-slide-index');
        this.track.insertBefore(clone, firstOriginal);
      });

      for (let i = 0; i < cloneCount; i++) {
        const sourceIndex = i % n;
        const clone = this.originalSlides[sourceIndex].cloneNode(true);
        clone.classList.add('slide-clone', 'slide-clone--after');
        clone.removeAttribute('data-slide-index');
        this.track.appendChild(clone);
      }

      this.infiniteOffset = cloneCount;
      this.currentSlide = 0;
    }

    findNavButtons() {
      let prevBtn = this.querySelector('.minimal-slider__btn--prev');
      let nextBtn = this.querySelector('.minimal-slider__btn--next');

      if (prevBtn && nextBtn) {
        return { prev: prevBtn, next: nextBtn };
      }

      const sliderId = this.id;
      if (sliderId) {
        prevBtn = document.querySelector(`[data-slider-target="${sliderId}"] .minimal-slider__btn--prev`);
        nextBtn = document.querySelector(`[data-slider-target="${sliderId}"] .minimal-slider__btn--next`);

        if (prevBtn && nextBtn) {
          return { prev: prevBtn, next: nextBtn };
        }
      }

      return { prev: null, next: null };
    }

    findDots() {
      let dots = this.querySelectorAll('.minimal-slider__dot');

      if (dots.length > 0) {
        return dots;
      }

      const sliderId = this.id;
      if (sliderId) {
        dots = document.querySelectorAll(`[data-slider-target="${sliderId}"] .minimal-slider__dot`);

        if (dots.length > 0) {
          return dots;
        }
      }

      return [];
    }

    init() {
      if (!this.track) return;

      this.totalSlides = this.infiniteLoop ? this.totalOriginalSlides : this.slides.length;

      this.cacheDOM();
      this.setupAriaLiveRegion();

      if (this.infiniteLoop && this.infiniteOffset) {
        this.track.style.transition = 'none';
        requestAnimationFrame(() => {
          this.applySlideTransform(null, this.infiniteOffset);
          this.track.offsetHeight;
          this.track.style.transition = '';
        });
      }

      this.originalSlides.forEach((slide, i) => {
        if (i === 0) {
          slide.classList.add('slide--active');
        } else {
          slide.classList.remove('slide--active');
        }
      });

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

      this.initDotsNavigation();
      this.initProgressBars();
      this.setupTouchEvents();
      this.setupClickNavigation();
      this.setupKeyboardNavigation();
      this.setupTransitionListener();
      this.updateNavigationVisibility();
      this.updateSpotlightDistances();
      this.setupResizeListener();

      if (this.autoplay) {
        this.startAutoplay();
        this.addEventListener('mouseenter', () => this.pauseAutoplay());
        this.addEventListener('mouseleave', () => this.resumeAutoplay());
      }

      if (this.animationType === 'product') {
        this.setupVariantListener();
        this.setupAllThumbnailsClick();

        if (this.classList.contains('product-media-slider--thumb-peek')) {
          this.setupThumbPeek();
        }
      }
    }

    cacheDOM() {
      this.cachedElements = {
        externalContainer: null,
        progressBar: null,
        fractionCurrent: null
      };

      if (this.id) {
        this.cachedElements.externalContainer = document.querySelector(`[data-slider-target="${this.id}"]`);
      }

      this.cachedElements.progressBar = this.querySelector('[data-progress-bar]');
      if (!this.cachedElements.progressBar && this.cachedElements.externalContainer) {
        this.cachedElements.progressBar = this.cachedElements.externalContainer.querySelector('[data-progress-bar]');
      }

      this.cachedElements.fractionCurrent = this.querySelector('[data-current-slide]');
      if (!this.cachedElements.fractionCurrent && this.cachedElements.externalContainer) {
        this.cachedElements.fractionCurrent = this.cachedElements.externalContainer.querySelector('[data-current-slide]');
      }
    }

    setupAriaLiveRegion() {
      this.ariaLiveRegion = document.createElement('div');
      this.ariaLiveRegion.setAttribute('aria-live', 'polite');
      this.ariaLiveRegion.setAttribute('aria-atomic', 'true');
      this.ariaLiveRegion.className = 'visually-hidden';
      this.appendChild(this.ariaLiveRegion);
    }

    announceSlide(slideIndex) {
      if (!this.ariaLiveRegion) return;

      const totalSlides = this.totalOriginalSlides || this.totalSlides;
      this.ariaLiveRegion.textContent = `Slide ${slideIndex + 1} of ${totalSlides}`;
    }

    setupThumbPeek() {
      const container = this.querySelector('.minimal-slider__container');
      if (!container) return;

      this.slides.forEach((slide) => {
        slide.addEventListener('click', () => {
          const slideIndex = parseInt(slide.dataset.slideIndex);
          if (!isNaN(slideIndex) && slideIndex !== this.currentSlide) {
            this.syncAllSlidersInSection(slideIndex);
          }
        });
      });

      this.updateThumbPeekPosition();
    }

    updateThumbPeekPosition() {
      if (!this.classList.contains('product-media-slider--thumb-peek')) return;
      if (!this.track) return;

      const activeSlide = this.slides[this.currentSlide];
      if (!activeSlide) return;

      const slideWidth = activeSlide.offsetWidth;
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      const translateX = -(this.currentSlide * (slideWidth + gap));

      this.track.style.transform = `translateX(${translateX}px)`;
    }

    scrollToActiveSlide() {
      this.updateThumbPeekPosition();
    }

    getSlidesPerView() {
      if (this.cachedSlidesPerView !== null) {
        return this.cachedSlidesPerView;
      }

      const computedStyle = getComputedStyle(this.track);
      const mobileSlidesVar = computedStyle.getPropertyValue('--slides-per-view-mobile').trim();
      const tabletSlidesVar = computedStyle.getPropertyValue('--slides-per-view-tablet').trim();
      const desktopSlidesVar = computedStyle.getPropertyValue('--slides-per-view-desktop').trim();

      const mobileSlides = parseInt(mobileSlidesVar) || 1;
      const tabletSlides = parseInt(tabletSlidesVar) || 2;
      const desktopSlides = parseInt(desktopSlidesVar) || 3;

      let result;
      if (window.innerWidth >= CONSTANTS.BREAKPOINT_DESKTOP) {
        result = desktopSlides;
      } else if (window.innerWidth >= CONSTANTS.BREAKPOINT_TABLET) {
        result = tabletSlides;
      } else {
        result = mobileSlides;
      }

      this.cachedSlidesPerView = result;
      return result;
    }

    applySlideTransform(slideIndex, physicalIndex = null) {
      if (!this.track) return;

      const isMultiSlide = this.classList.contains('multi-slide-slider');
      const isCoverflow = this.classList.contains('carousel--coverflow');
      const targetIndex = physicalIndex !== null ? physicalIndex : (slideIndex !== null ? slideIndex : 0);

      if (isMultiSlide) {
        const firstSlide = this.slides[0];
        if (!firstSlide) return;

        const slideWidth = firstSlide.offsetWidth;
        const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
        let step = slideWidth + gap;

        if (isCoverflow) {
          const slideStyle = getComputedStyle(firstSlide);
          const marginLeft = parseFloat(slideStyle.marginLeft) || 0;
          const marginRight = parseFloat(slideStyle.marginRight) || 0;
          step += marginLeft + marginRight;
        }

        let translateX = -(targetIndex * step);

        if (isCoverflow) {
          const container = this.querySelector('.minimal-slider__container');
          if (container) {
            const firstMarginLeft = parseFloat(getComputedStyle(firstSlide).marginLeft) || 0;
            translateX += (container.offsetWidth - slideWidth) / 2 - firstMarginLeft;
          }
        }

        this.track.style.transform = `translateX(${translateX}px)`;
      } else {
        const translateX = -100 * targetIndex;
        this.track.style.transform = `translateX(${translateX}%)`;
      }
    }

    goToSlide(index) {
      if (index < 0 || index >= this.totalSlides || index === this.currentSlide) {
        return;
      }

      this.isUserInteracting = true;
      this.startTransition();
      this.currentSlide = index;

      this.originalSlides.forEach((slide, i) => {
        if (i === index) {
          slide.classList.add('slide--active');
        } else {
          slide.classList.remove('slide--active');
        }
      });

      if (this.animationType === 'slide' && this.track) {
        this.applySlideTransform(this.currentSlide);
      }

      this.updateDots();
      this.updateNavigationVisibility();
      this.updateSpotlightDistances();
      this.announceSlide(index);
      this.dispatchSlideEvent('slideChange');
      this.syncProductThumbnails();

      if (this.classList.contains('product-media-slider--thumb-peek')) {
        this.scrollToActiveSlide();
      }

      if (this.autoplay) {
        this.resetAutoplay();
      }
      this.isUserInteracting = false;
    }

    setupResizeListener() {
      this.resizeHandler = this.debounce(() => {
        this.cachedSlidesPerView = null;
        this.updateSpotlightDistances();

        if (this.classList.contains('multi-slide-slider') && this.track) {
          requestAnimationFrame(() => {
            this.track.style.transition = 'none';
            const physicalIndex = this.infiniteLoop && typeof this.infinitePhysicalIndex !== 'undefined'
              ? this.infinitePhysicalIndex
              : this.currentSlide;
            this.applySlideTransform(this.currentSlide, this.infiniteLoop ? physicalIndex : null);
            requestAnimationFrame(() => {
              this.track.style.transition = '';
            });
          });
        }
      }, CONSTANTS.RESIZE_DEBOUNCE_MS);

      window.addEventListener('resize', this.resizeHandler);
    }

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func.apply(this, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    updateSpotlightDistances() {
      const isSpotlight = this.classList.contains('carousel--spotlight');
      const isCoverflow = this.classList.contains('carousel--coverflow');
      if (!isSpotlight && !isCoverflow) return;

      const slidesPerView = this.getSlidesPerView();

      if (!this.infiniteLoop) {
        const currentPhysicalIndex = this.currentSlide;
        const isEven = slidesPerView % 2 === 0;
        const centerIndex = isCoverflow
          ? currentPhysicalIndex
          : currentPhysicalIndex + Math.floor(slidesPerView / 2);
        const centerLeft = isEven
          ? (isCoverflow ? currentPhysicalIndex : currentPhysicalIndex + Math.floor(slidesPerView / 2) - 1)
          : null;
        const centerRight = isEven ? (centerLeft !== null ? centerLeft + 1 : null) : null;

        this.originalSlides.forEach((slide, slideIndex) => {
          let distance;

          if (isEven) {
            const distanceLeft = Math.abs(slideIndex - centerLeft);
            const distanceRight = Math.abs(slideIndex - centerRight);
            distance = Math.min(distanceLeft, distanceRight);
          } else {
            distance = Math.abs(slideIndex - centerIndex);
          }

          distance = Math.min(distance, 3);
          slide.setAttribute('data-distance', distance);

          if (isCoverflow) {
            const refPoint = isEven ? (centerLeft + centerRight) / 2 : centerIndex;
            if (slideIndex < refPoint) {
              slide.setAttribute('data-side', 'left');
            } else if (slideIndex > refPoint) {
              slide.setAttribute('data-side', 'right');
            } else {
              slide.removeAttribute('data-side');
            }
          }
        });
      } else {
        const n = this.totalOriginalSlides;
        const logicalCenterIndex = this.currentSlide;
        const isEven = slidesPerView % 2 === 0;

        const centerIndexLogical = isCoverflow ? logicalCenterIndex : logicalCenterIndex + Math.floor(slidesPerView / 2);
        const centerLeftLogical = isEven
          ? (isCoverflow ? logicalCenterIndex : logicalCenterIndex + Math.floor(slidesPerView / 2) - 1)
          : null;
        const centerRightLogical = isEven ? (centerLeftLogical !== null ? centerLeftLogical + 1 : null) : null;

        const distanceMap = new Map();

        this.originalSlides.forEach((originalSlide, logicalIndex) => {
          let distance;
          if (isEven) {
            const distanceLeft = this.circularDistance(logicalIndex, centerLeftLogical, n);
            const distanceRight = this.circularDistance(logicalIndex, centerRightLogical, n);
            distance = Math.min(distanceLeft, distanceRight);
          } else {
            distance = this.circularDistance(logicalIndex, centerIndexLogical, n);
          }

          distance = Math.min(distance, 3);
          distanceMap.set(logicalIndex, distance);
          originalSlide.setAttribute('data-distance', distance);

          if (isCoverflow) {
            const refPoint = isEven && centerLeftLogical !== null && centerRightLogical !== null
              ? (centerLeftLogical + centerRightLogical) / 2
              : centerIndexLogical;

            const diff = (logicalIndex - refPoint + n) % n;
            if (diff > 0 && diff < n / 2) {
              originalSlide.setAttribute('data-side', 'right');
            } else if (diff > n / 2) {
              originalSlide.setAttribute('data-side', 'left');
            } else {
              originalSlide.removeAttribute('data-side');
            }
          }
        });

        this.slides.forEach((slide) => {
          if (!slide.hasAttribute('data-slide-index')) {
            const physicalIndex = Array.from(this.slides).indexOf(slide);
            const logicalIndex = this.getLogicalIndexFromPhysical(physicalIndex);

            const distance = distanceMap.get(logicalIndex);
            if (distance !== undefined) {
              slide.setAttribute('data-distance', distance);

              if (isCoverflow) {
                const refPoint = isEven && centerLeftLogical !== null && centerRightLogical !== null
                  ? (centerLeftLogical + centerRightLogical) / 2
                  : centerIndexLogical;

                const diff = (logicalIndex - refPoint + n) % n;
                if (diff > 0 && diff < n / 2) {
                  slide.setAttribute('data-side', 'right');
                } else if (diff > n / 2) {
                  slide.setAttribute('data-side', 'left');
                } else {
                  slide.removeAttribute('data-side');
                }
              }
            }
          }
        });
      }
    }

    getLogicalIndexFromPhysical(physicalIndex) {
      if (!this.infiniteLoop || !this.infiniteOffset) return physicalIndex;
      const offset = physicalIndex - this.infiniteOffset;
      const n = this.totalOriginalSlides;
      return ((offset % n) + n) % n;
    }

    circularDistance(a, b, n) {
      const diff = Math.abs(a - b);
      return Math.min(diff, n - diff);
    }

    setupTransitionListener() {
      if (!this.track) return;

      this.transitionEndHandler = (e) => {
        if (e.target !== this.track || e.propertyName !== 'transform') return;

        clearTimeout(this.transitionTimeout);
        this.isTransitioning = false;
        this.processNavigationQueue();
      };

      this.track.addEventListener('transitionend', this.transitionEndHandler);
    }

    startTransition() {
      this.isTransitioning = true;

      clearTimeout(this.transitionTimeout);
      this.transitionTimeout = setTimeout(() => {
        if (this.isTransitioning) {
          console.warn('[MinimalSlider] Transition timeout - forcing unlock');
          this.isTransitioning = false;
          this.processNavigationQueue();
        }
      }, CONSTANTS.TRANSITION_TIMEOUT_MS);
    }

    queueNavigation(action) {
      this.navigationQueue = [action];
    }

    processNavigationQueue() {
      clearTimeout(this.transitionTimeout);
      if (this.isTransitioning || this.navigationQueue.length === 0) return;

      const nextAction = this.navigationQueue.shift();
      if (nextAction) {
        nextAction();
      }
    }

    jumpToPhysicalIndex(physicalIndex, withTransition = false) {
      if (!withTransition) {
        this.classList.add('no-transition');
        this.track.offsetHeight;
      }

      this.applySlideTransform(null, physicalIndex);

      if (!withTransition) {
        this.track.offsetHeight;
        this.classList.remove('no-transition');
      }
    }

    next() {
      if (this.infiniteLoop) {
        this.goToSlideInfinite(this.currentSlide + 1);
      } else if (this.loop) {
        let lastSlideIndex = this.totalSlides - 1;

        if (this.classList.contains('multi-slide-slider')) {
          const slidesPerView = this.getSlidesPerView();
          lastSlideIndex = Math.max(0, this.totalSlides - slidesPerView);
        }

        const nextIndex = this.currentSlide === lastSlideIndex ? 0 : this.currentSlide + 1;
        this.goToSlide(nextIndex);
      } else {
        let maxSlide = this.totalSlides - 1;

        if (this.classList.contains('multi-slide-slider')) {
          const slidesPerView = this.getSlidesPerView();
          maxSlide = Math.max(0, this.totalSlides - slidesPerView);
        }

        if (this.currentSlide < maxSlide) {
          this.goToSlide(this.currentSlide + 1);
        }
      }
    }

    prev() {
      if (this.infiniteLoop) {
        this.goToSlideInfinite(this.currentSlide - 1);
      } else if (this.loop) {
        let lastSlideIndex = this.totalSlides - 1;

        if (this.classList.contains('multi-slide-slider')) {
          const slidesPerView = this.getSlidesPerView();
          lastSlideIndex = Math.max(0, this.totalSlides - slidesPerView);
        }

        const prevIndex = this.currentSlide === 0 ? lastSlideIndex : this.currentSlide - 1;
        this.goToSlide(prevIndex);
      } else {
        if (this.currentSlide > 0) {
          this.goToSlide(this.currentSlide - 1);
        }
      }
    }

    _updateSlideState(newLogicalIndex) {
      this.currentSlide = newLogicalIndex;

      this.originalSlides.forEach((slide, i) => {
        slide.classList.toggle('slide--active', i === newLogicalIndex);
      });

      if (this.animationType === 'slide' && this.track) {
        this.applySlideTransform(this.currentSlide, this.infinitePhysicalIndex);
      }

      this.updateDots();
      this.updateNavigationVisibility();
      this.updateSpotlightDistances();
      this.announceSlide(newLogicalIndex);
      this.dispatchSlideEvent('slideChange');
      this.syncProductThumbnails();

      if (this.autoplay) {
        this.resetAutoplay();
      }
    }

    goToSlideInfinite(logicalIndex) {
      this.isUserInteracting = true;
      this.startTransition();

      if (typeof this.infinitePhysicalIndex === 'undefined') {
        this.infinitePhysicalIndex = this.infiniteOffset;
      }

      const firstOriginalIndex = this.infiniteOffset;
      const lastOriginalIndex = this.infiniteOffset + this.totalOriginalSlides - 1;

      const direction = logicalIndex > this.currentSlide ? 1 : (logicalIndex < this.currentSlide ? -1 : 0);
      if (direction === 0) return;

      if (direction > 0 && this.infinitePhysicalIndex >= lastOriginalIndex) {
        this.infinitePhysicalIndex = firstOriginalIndex + (this.infinitePhysicalIndex - lastOriginalIndex) - 1;
        this.jumpToPhysicalIndex(this.infinitePhysicalIndex, false);
      } else if (direction < 0 && this.infinitePhysicalIndex <= firstOriginalIndex) {
        this.infinitePhysicalIndex = lastOriginalIndex - (firstOriginalIndex - this.infinitePhysicalIndex) + 1;
        this.jumpToPhysicalIndex(this.infinitePhysicalIndex, false);
      }

      this.infinitePhysicalIndex += direction;

      const physicalOffset = this.infinitePhysicalIndex - firstOriginalIndex;
      const normalizedIndex = ((physicalOffset % this.totalOriginalSlides) + this.totalOriginalSlides) % this.totalOriginalSlides;

      this._updateSlideState(normalizedIndex);
      this.isUserInteracting = false;
    }

    goToSlideInfiniteDirect(targetIndex) {
      if (targetIndex === this.currentSlide) return;

      this.startTransition();

      if (typeof this.infinitePhysicalIndex === 'undefined') {
        this.infinitePhysicalIndex = this.infiniteOffset;
      }

      const firstOriginalIndex = this.infiniteOffset;
      this.infinitePhysicalIndex = firstOriginalIndex + targetIndex;

      this._updateSlideState(targetIndex);
    }

    initDotsNavigation() {
      let dotsContainer = this.querySelector('.minimal-slider__dots-container');

      if (!dotsContainer && this.id) {
        const externalContainer = document.querySelector(`[data-slider-target="${this.id}"]`);
        if (externalContainer) {
          dotsContainer = externalContainer.querySelector('.minimal-slider__dots-container');
        }
      }

      if (!dotsContainer) return;

      dotsContainer.addEventListener('click', (e) => {
        const dot = e.target.closest('.minimal-slider__dot');
        if (!dot) return;

        let index = dot.dataset.slideDot !== undefined
          ? parseInt(dot.dataset.slideDot)
          : Array.from(this.dots).indexOf(dot);

        if (index < 0 || isNaN(index)) return;

        if (this.infiniteLoop) {
          this.goToSlideInfiniteDirect(index);
        } else {
          this.goToSlide(index);
        }
      });
    }

    initProgressBars() {
      let container = this.querySelector('.banner-slider__progress-bars');
      if (!container && this.id) {
        const ext = document.querySelector(`[data-slider-target="${this.id}"]`);
        if (ext) container = ext.querySelector('.banner-slider__progress-bars');
      }
      if (!container) return;

      this.progressBarsContainer = container;
      this.progressBarsItems = container.querySelectorAll('.banner-slider__progress-bar');

      if (this.autoplay) {
        this.style.setProperty('--autoplay-speed', this.autoplaySpeed + 'ms');
      }

      container.addEventListener('click', (e) => {
        const bar = e.target.closest('.banner-slider__progress-bar');
        if (!bar) return;
        const index = parseInt(bar.dataset.slideIndex);
        if (isNaN(index)) return;
        if (this.infiniteLoop) {
          this.goToSlideInfiniteDirect(index);
        } else {
          this.goToSlide(index);
        }
      });
    }

    updateProgressBarsState() {
      if (!this.progressBarsItems || this.progressBarsItems.length === 0) return;
      this.progressBarsItems.forEach((bar, i) => {
        const isActive = i === this.currentSlide;
        bar.classList.toggle('banner-slider__progress-bar--active', isActive);
        if (isActive) {
          const fill = bar.querySelector('.banner-slider__progress-fill');
          if (fill) {
            fill.style.animation = 'none';
            fill.offsetHeight;
            fill.style.animation = '';
          }
        }
      });
    }

    restartActiveProgressBar() {
      if (!this.progressBarsContainer) return;
      const fill = this.progressBarsContainer.querySelector('.banner-slider__progress-bar--active .banner-slider__progress-fill');
      if (fill) {
        fill.style.animation = 'none';
        fill.offsetHeight;
        fill.style.animation = '';
      }
    }

    updateDots() {
      if (this.dots.length > 0) {
        this.dots.forEach((dot, i) => dot.classList.toggle('minimal-slider__dot--active', i === this.currentSlide));
      }
      this.updateProgressBar();
      this.updateFraction();
      this.updateProgressBarsState();
    }

    updateProgressBar() {
      if (!this.cachedElements || !this.cachedElements.progressBar) return;

      const totalSlides = this.totalOriginalSlides || this.totalSlides;
      const progressWidth = ((this.currentSlide + 1) / totalSlides) * 100;
      this.cachedElements.progressBar.style.width = `${progressWidth}%`;
    }

    updateFraction() {
      if (!this.cachedElements || !this.cachedElements.fractionCurrent) return;

      this.cachedElements.fractionCurrent.textContent = this.currentSlide + 1;
    }

    updateNavigationVisibility() {
      if (this.loop) {
        if (this.prevBtn) this.prevBtn.style.visibility = 'visible';
        if (this.nextBtn) this.nextBtn.style.visibility = 'visible';
        return;
      }

      const isMultiSlide = this.classList.contains('multi-slide-slider');
      const maxSlide = isMultiSlide ? Math.max(0, this.totalSlides - this.getSlidesPerView()) : this.totalSlides - 1;

      if (this.prevBtn) this.prevBtn.style.visibility = this.currentSlide === 0 ? 'hidden' : 'visible';
      if (this.nextBtn) this.nextBtn.style.visibility = this.currentSlide >= maxSlide ? 'hidden' : 'visible';
    }

    startAutoplay() {
      if (!this.autoplay) return;
      this.autoplayTimer = setInterval(() => {
        if (!this.isUserInteracting) {
          this.next();
          if (!this.loop && this.currentSlide === this.totalSlides - 1) this.pauseAutoplay();
        }
      }, this.autoplaySpeed);
    }

    pauseAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    resumeAutoplay() {
      if (this.autoplay && !this.autoplayTimer) {
        this.restartActiveProgressBar();
        this.startAutoplay();
      }
    }

    resetAutoplay() {
      if (!this.autoplay) return;
      this.pauseAutoplay();
      this.startAutoplay();
    }

    setupTouchEvents() {
      if (!this.track) return;

      let startX = 0;
      let startY = 0;
      let isDragging = false;

      this.track.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isDragging = true;
      }, { passive: true });

      this.track.addEventListener('touchmove', (e) => {
        if (!isDragging) return;

        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        const diffX = Math.abs(currentX - startX);
        const diffY = Math.abs(currentY - startY);

        if (diffX > diffY) {
          e.preventDefault();
        }
      }, { passive: false });

      this.track.addEventListener('touchend', (e) => {
        if (!isDragging) return;
        isDragging = false;

        const endX = e.changedTouches[0].clientX;
        const diffX = startX - endX;

        if (Math.abs(diffX) > CONSTANTS.SWIPE_THRESHOLD_PX) {
          if (diffX > 0) {
            this.next();
          } else {
            this.prev();
          }
        }
      }, { passive: true });
    }

    setupClickNavigation() {
      if (!this.clickNavigation || !this.track) return;

      this.track.addEventListener('click', (e) => {
        const clickedElement = e.target;

        if (clickedElement.closest('a, button, [role="button"], [data-video-play]')) {
          return;
        }

        const clickedSlide = clickedElement.closest('.minimal-slider__slide');
        if (!clickedSlide) return;

        const clickedIndex = Array.from(this.slides).indexOf(clickedSlide);
        if (clickedIndex === -1) return;

        const centerSlideIndex = this.infiniteLoop
          ? (this.infiniteOffset !== undefined ? this.infiniteOffset + this.currentSlide : this.currentSlide)
          : this.currentSlide;

        if (this.isTransitioning) {
          if (clickedIndex === centerSlideIndex) {
            this.queueNavigation(() => this.next());
          } else {
            if (this.infiniteLoop) {
              const targetIndex = clickedIndex - (this.infiniteOffset || 0);
              this.queueNavigation(() => this.goToSlideInfiniteDirect(targetIndex));
            } else {
              this.queueNavigation(() => this.goToSlide(clickedIndex));
            }
          }
        } else {
          if (clickedIndex === centerSlideIndex) {
            this.next();
          } else {
            if (this.infiniteLoop) {
              this.goToSlideInfiniteDirect(clickedIndex - (this.infiniteOffset || 0));
            } else {
              this.goToSlide(clickedIndex);
            }
          }
        }
      });
    }

    setupKeyboardNavigation() {
      this.setAttribute('tabindex', '0');
      this.setAttribute('role', 'region');
      this.setAttribute('aria-label', 'Carousel');

      this.addEventListener('keydown', (e) => {
        if (this.isTransitioning) return;

        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            this.prev();
            break;
          case 'ArrowRight':
            e.preventDefault();
            this.next();
            break;
          case 'Home':
            e.preventDefault();
            if (this.infiniteLoop) {
              this.goToSlideInfiniteDirect(0);
            } else {
              this.goToSlide(0);
            }
            break;
          case 'End':
            e.preventDefault();
            const lastIndex = this.totalSlides - 1;
            if (this.infiniteLoop) {
              this.goToSlideInfiniteDirect(lastIndex);
            } else {
              this.goToSlide(lastIndex);
            }
            break;
        }
      });
    }

    syncThumbnails(thumbnailsSelector) {
      const thumbnails = document.querySelectorAll(thumbnailsSelector);
      thumbnails.forEach((thumb, index) => {
        thumb.classList.toggle('active', index === this.currentSlide);
      });
    }

    syncProductThumbnails() {
      if (this.animationType !== 'product') return;

      const section = this.closest('.section');
      const sectionId = section ? section.dataset.sectionId : null;

      if (!sectionId) return;

      const sliderId = this.id;
      if (sliderId) {
        const thumbnailsId = sliderId.replace('product-media-', 'product-thumbnails-');
        const directThumbnails = document.querySelectorAll(`#${thumbnailsId} .product-thumbnail`);

        directThumbnails.forEach((thumb, index) => {
          thumb.classList.toggle('active', index === this.currentSlide);
        });

        const directContainer = document.querySelector(`#${thumbnailsId}`);
        if (directContainer && !directContainer.classList.contains('product-thumbnails--grid')) {
          this.scrollThumbnailsContainer(directContainer, this.currentSlide);
        }
      }

      const allSectionThumbnails = section.querySelectorAll('.product-thumbnail[data-thumb-index]');

      allSectionThumbnails.forEach((thumb) => {
        const thumbIndex = parseInt(thumb.dataset.thumbIndex);
        thumb.classList.toggle('active', thumbIndex === this.currentSlide);
      });

      const allThumbnailContainers = section.querySelectorAll('.product-thumbnails');
      allThumbnailContainers.forEach(container => {
        if (!container.classList.contains('product-thumbnails--grid')) {
          this.scrollThumbnailsContainer(container, this.currentSlide);
        }
      });
    }

    scrollThumbnailsContainer(container, activeIndex) {
      const activeThumb = container.querySelector(`[data-thumb-index="${activeIndex}"], .product-thumbnail:nth-child(${activeIndex + 1})`);

      if (activeThumb) {
        const containerWidth = container.offsetWidth;
        const thumbLeft = activeThumb.offsetLeft;
        const thumbWidth = activeThumb.offsetWidth;
        const scrollLeft = thumbLeft - (containerWidth / 2) + (thumbWidth / 2);

        container.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth'
        });
      }
    }

    setupThumbnailsSync(thumbnailsContainer) {
      if (!thumbnailsContainer) return;

      thumbnailsContainer.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-thumb-index]');
        if (!thumb) return;

        const index = parseInt(thumb.dataset.thumbIndex);
        if (!isNaN(index)) {
          this.goToSlide(index);
        }
      });

      this.addEventListener('slideChange', () => {
        this.syncThumbnails(`${thumbnailsContainer.tagName.toLowerCase()}${thumbnailsContainer.className ? '.' + thumbnailsContainer.className.split(' ').join('.') : ''} [data-thumb-index]`);
      });

      this.syncThumbnails(`${thumbnailsContainer.tagName.toLowerCase()}${thumbnailsContainer.className ? '.' + thumbnailsContainer.className.split(' ').join('.') : ''} [data-thumb-index]`);
    }

    dispatchSlideEvent(eventType) {
      const event = new CustomEvent(eventType, {
        detail: {
          currentSlide: this.currentSlide,
          totalSlides: this.totalSlides,
          slider: this
        },
        bubbles: true
      });
      this.dispatchEvent(event);
    }

    setupVariantListener() {
      const section = this.closest('.section');
      const sectionId = section ? section.dataset.sectionId : null;

      if (!sectionId) return;

      if (section.hasAttribute('data-variant-listener-setup')) return;
      section.setAttribute('data-variant-listener-setup', 'true');

      document.addEventListener('variant:change', (event) => {
        const { variant, sectionId: eventSectionId } = event.detail;

        if (eventSectionId !== sectionId) return;

        if (variant && variant.featured_media && variant.featured_media.position) {
          const mediaIndex = variant.featured_media.position - 1;
          this.syncAllSlidersInSection(mediaIndex);
        }
      });
    }

    setupAllThumbnailsClick() {
      const section = this.closest('.section');
      if (!section) return;

      if (section.hasAttribute('data-thumbnails-setup')) return;
      section.setAttribute('data-thumbnails-setup', 'true');

      const allThumbnails = section.querySelectorAll('.product-thumbnail[data-thumb-index]');

      allThumbnails.forEach((thumb) => {
        const thumbIndex = parseInt(thumb.dataset.thumbIndex);

        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          this.syncAllSlidersInSection(thumbIndex);
        });

        thumb.style.cursor = 'pointer';
      });
    }

    syncAllSlidersInSection(slideIndex) {
      const section = this.closest('.section');
      if (!section) return;

      const allProductSliders = section.querySelectorAll('minimal-slider[data-animation-type="product"]');

      allProductSliders.forEach(slider => {
        slider.goToSlide(slideIndex);
      });

      const allThumbnails = section.querySelectorAll('.product-thumbnail[data-thumb-index]');
      allThumbnails.forEach((thumb) => {
        const thumbIndex = parseInt(thumb.dataset.thumbIndex);
        thumb.classList.toggle('active', thumbIndex === slideIndex);
      });

      const allThumbnailContainers = section.querySelectorAll('.product-thumbnails');
      allThumbnailContainers.forEach(container => {
        if (!container.classList.contains('product-thumbnails--grid')) {
          this.scrollThumbnailsContainer(container, slideIndex);
        }
      });
    }

    api() {
      return {
        goTo: (index) => this.goToSlide(index),
        next: () => this.next(),
        prev: () => this.prev(),
        play: () => this.startAutoplay(),
        pause: () => this.pauseAutoplay(),
        getCurrentSlide: () => this.currentSlide,
        getTotalSlides: () => this.totalSlides,
        syncThumbnails: (selector) => this.syncThumbnails(selector),
        setupVariantListener: () => this.setupVariantListener(),
        setupAllThumbnailsClick: () => this.setupAllThumbnailsClick()
      };
    }
  });
}

